import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { listenToPaymentMethods, CardInfo } from "../../redux/paymentMethodSlice";
import { PlusCircle, CreditCard, X } from "lucide-react";
import { TextField, MenuItem, Button, Box } from "@mui/material";
import countries from "../../GLOBAL/Country";

const PaymentMethods: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { cards } = useSelector((state: RootState) => state.paymentMethods);

  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    cardNumber: "",
    expMonth: "",
    expYear: "",
    cvv: "",
    zip: "",
    country: "",
  });

  useEffect(() => {
    if (user?.userId) dispatch(listenToPaymentMethods(user.userId));
  }, [user, dispatch]);

  const handleChange = (key: string, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = () => {
    // Later integrate your Stripe or Firebase add logic
    console.log("Submitting card:", form);
    setExpanded(false);
    setForm({
      cardNumber: "",
      expMonth: "",
      expYear: "",
      cvv: "",
      zip: "",
      country: "",
    });
  };

  return (
    <div style={styles.container}>
      {/* Section Header */}
      <h2 style={styles.title}>Your Payment Methods</h2>

      {/* Existing Cards */}
      <div style={styles.cardsGrid}>
        {cards.length > 0 ? (
          cards.map((card: CardInfo, index) => (
            <motion.div
              key={index}
              style={styles.card}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <CreditCard size={24} color="#bf9b30" />
              <div style={{ marginLeft: 10 }}>
                <p style={styles.cardBrand}>{card.brand.toUpperCase()} •••• {card.last4}</p>
                <p style={styles.cardExpiry}>
                  Expires {card.expMonth}/{card.expYear} — {card.country}
                </p>
              </div>
            </motion.div>
          ))
        ) : (
          <p style={{ color: "#aaa", marginTop: 10 }}>No cards added yet.</p>
        )}
      </div>

      {/* Add Card Button */}
      <AnimatePresence initial={false}>
        {!expanded && (
          <motion.div
            style={styles.addCard}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setExpanded(true)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <PlusCircle size={24} />
            <span style={{ marginLeft: 10, fontWeight: 600 }}>Add Payment Method</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expanded Card Form */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -10 }}
            transition={{ duration: 0.3 }}
            style={styles.cardForm}
          >
            <div style={styles.formHeader}>
              <h3 style={styles.formTitle}>Add Card Details</h3>
              <X size={22} color="#fff" onClick={() => setExpanded(false)} style={{ cursor: "pointer" }} />
            </div>

            <TextField
              label="Card Number"
              fullWidth
              placeholder="1234 5678 9012 3456"
              value={form.cardNumber}
              onChange={(e) => handleChange("cardNumber", e.target.value)}
              sx={fieldStyle}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Expiration"
                placeholder="MM"
                value={form.expMonth}
                onChange={(e) => handleChange("expMonth", e.target.value)}
                sx={{ ...fieldStyle, flex: 1 }}
              />
              <TextField
                label="Year"
                placeholder="YY"
                value={form.expYear}
                onChange={(e) => handleChange("expYear", e.target.value)}
                sx={{ ...fieldStyle, flex: 1 }}
              />
              <TextField
                label="CVV"
                placeholder="***"
                value={form.cvv}
                onChange={(e) => handleChange("cvv", e.target.value)}
                sx={{ ...fieldStyle, flex: 1 }}
              />
            </Box>

            <TextField
              label="ZIP Code"
              fullWidth
              placeholder="12345"
              value={form.zip}
              onChange={(e) => handleChange("zip", e.target.value)}
              sx={fieldStyle}
            />

            <TextField
              select
              fullWidth
              label="Country"
              value={form.country}
              onChange={(e) => handleChange("country", e.target.value)}
              sx={fieldStyle}
            >
              {countries.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.emoji} {c.name}
                </MenuItem>
              ))}
            </TextField>

            <Button
              onClick={handleSubmit}
              fullWidth
              sx={{
                mt: 2,
                py: 1.4,
                borderRadius: "10px",
                fontWeight: 600,
                background: "linear-gradient(145deg, #bf9b30, #e6c35a)",
                color: "#000",
                "&:hover": {
                  background: "linear-gradient(145deg, #e6c35a, #bf9b30)",
                },
              }}
            >
              Done
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PaymentMethods;

// ─── Styles ───────────────────────────────────────────────
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: "30px 20px",
    color: "#fff",
    fontFamily: "'Poppins', sans-serif",
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    color: "#bf9b30",
    textTransform: "uppercase",
    marginBottom: 20,
  },
  cardsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    marginBottom: 30,
  },
  card: {
    display: "flex",
    alignItems: "center",
    background: "linear-gradient(145deg, #0a0a0a, #111)",
    border: "1px solid rgba(191,155,48,0.25)",
    padding: "14px 18px",
    borderRadius: 14,
    boxShadow: "inset 0 2px 8px rgba(0,0,0,0.6)",
  },
  cardBrand: {
    fontSize: 15,
    fontWeight: 600,
    color: "#e6c35a",
  },
  cardExpiry: {
    fontSize: 12,
    color: "#aaa",
  },
  addCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(145deg, #0a0a0a, #111)",
    border: "1px solid rgba(191,155,48,0.4)",
    borderRadius: 14,
    padding: "14px",
    color: "#bf9b30",
    fontSize: 15,
    cursor: "pointer",
    boxShadow: "0 0 10px rgba(191,155,48,0.3)",
  },
  cardForm: {
    background: "linear-gradient(145deg, #0a0a0a, #111)",
    borderRadius: 16,
    padding: "24px 18px",
    border: "1px solid rgba(191,155,48,0.25)",
    boxShadow: "0 0 15px rgba(191,155,48,0.2)",
  },
  formHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  formTitle: {
    fontSize: 17,
    fontWeight: 600,
    color: "#e6c35a",
  },
};

const fieldStyle = {
  mb: 2,
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#ddd" },
    "&:hover fieldset": { borderColor: "#bf9b30" },
    "&.Mui-focused fieldset": { borderColor: "#bf9b30" },
  },
  "& .MuiInputLabel-root": {
    color: "#555",
  },
};
